import Dialog from '@comp/Dialog/Dialog';
import styled from '@emotion/styled';
import { useCallback, useMemo } from 'react';
import Button from 'aquilla/core/molecules/Button';
import { segment } from '@actions/analytics';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMe, getIsLoggedIn } from '@api/selectors/auth';
import { mediaQuery } from '@styles/index';
import { useTestSeriesData } from './useTestSeriesData';
import { POSSIBLE_F1, POSSIBLE_F1_UID, UAITS_EVENT_MAP } from './constants';
import CenterSelectionFlow, { useDropdown } from './CenterSelectionFlow';

// #region Styles
const CenterSelectionModalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 500px;
  padding: 24px;

  ${mediaQuery.uptoMobile} {
    width: 100%;
  }
`;

const Title = styled.p`
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  letter-spacing: -0.5%;
  vertical-align: middle;
  color: var(--color-text-primary);
`;

const Subtitle = styled.p`
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0%;
  vertical-align: middle;
  color: var(--color-text-secondary);
`;

const CenterSelectionModal = () => {
  const meData = useMyInfo(getMe);
  const isLoggedIn = useMyInfo(getIsLoggedIn);

  const {
    showCenterSelectionModal,
    setShowCenterSelectionModal,
    selectedF1,
    selectedF2
  } = useTestSeriesData();

  const cardData = showCenterSelectionModal?.data;
  const centerList = useMemo(
    () => cardData?.csvDetails || [],
    [cardData?.csvDetails]
  );

  const {
    stateOptions,
    cityOptions,
    centerOptions,
    selectedState,
    selectedCity,
    selectedCenter,
    setSelectedState,
    setSelectedCity,
    setSelectedCenter
  } = useDropdown({ centerList });

  const handleClose = useCallback(() => {
    setShowCenterSelectionModal({
      show: false
    });
    setSelectedCity(null);
    setSelectedState(null);
    setSelectedCenter(null);
  }, [
    setSelectedCenter,
    setSelectedCity,
    setSelectedState,
    setShowCenterSelectionModal
  ]);

  const handleProceedClick = useCallback(() => {
    if (!selectedCenter) return;
    const { f1, f2, title } = cardData;
    segment.track(UAITS_EVENT_MAP.CLICKED_ON_SELECT_LOCATION, {
      userId: meData?.uid,
      current_page_url: window.location.href,
      login_status: isLoggedIn,
      page_title: 'UAITS',
      goal_name: f1,
      goal_uid: POSSIBLE_F1_UID[POSSIBLE_F1.indexOf(f1)],
      learner_class: f2,
      selected_state_name: selectedState?.value,
      selected_city_name: selectedCity?.value,
      selected_test_centre_name: selectedCenter?.value,
      test_name: title
    });

    const filteredDetails = centerList.filter(
      (item) =>
        item.test_center_name &&
        (selectedCenter?.value).includes(item.test_center_name) &&
        item.state === selectedState?.value &&
        item.city === selectedCity?.value
    );

    if (!filteredDetails || filteredDetails.length === 0) {
      return;
    }

    const selectedSkuUrl = filteredDetails[0]?.sku_url;
    if (selectedSkuUrl) {
      window.open(selectedSkuUrl, '_blank');
    }
    handleClose();
  }, [
    cardData,
    centerList,
    handleClose,
    isLoggedIn,
    meData?.uid,
    selectedCenter,
    selectedCity?.value,
    selectedState?.value
  ]);

  if (!showCenterSelectionModal.show || !showCenterSelectionModal.data)
    return null;
  return (
    <Dialog
      onClose={handleClose}
      mWebFullWidth
      center
      dialogState={showCenterSelectionModal.show}
      heading="Select Test Center"
      message="Please select a test center to proceed"
    >
      <CenterSelectionModalWrapper>
        <Title>Select your preferred test centre</Title>
        <Subtitle>
          Select the centre where you want to attempt your offline tests
        </Subtitle>
        <CenterSelectionFlow
          type="select-test-center"
          stateOptions={stateOptions}
          cityOptions={cityOptions}
          centerOptions={centerOptions}
          selectedState={selectedState}
          selectedCity={selectedCity}
          selectedCenter={selectedCenter}
          setSelectedState={setSelectedState}
          setSelectedCity={setSelectedCity}
          setSelectedCenter={setSelectedCenter}
        />

        <Button
          variant="primary"
          size="medium"
          label="Proceed"
          disabled={!selectedCenter}
          onClick={handleProceedClick}
        />
      </CenterSelectionModalWrapper>
    </Dialog>
  );
};

export default CenterSelectionModal;
